import { useMemo, useState } from 'react';
import { Table, Button, Badge, Form, Modal, Row, Col, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  useListReturnsQuery,
  useUpdateReturnStatusMutation,
  useCreateReturnLabelMutation,
  useGetReturnRatesMutation,
  useSelectReturnRateMutation,
} from '../../slices/returnsApiSlice';
import BackButton from "../../Components/BackButton";

const StatusBadge = ({ s }) => {
  const map = {
    requested: 'secondary',
    authorized: 'primary',
    rejected: 'danger',
    received: 'warning',
    refunded: 'success',
    partial_refunded: 'info',
    closed: 'dark',
  };
  return <Badge bg={map[s] || 'secondary'}>{s || 'requested'}</Badge>;
};

const norm = (s) => String(s || '').toLowerCase().replace(/\s+/g, '');

const REASON_LABELS = {
  wrong_item: 'Wrong item ordered',
  not_needed: 'No longer needed',
  damaged: 'Damaged in transit',
  defective: 'Defective / not working',
  other: 'Other',
};

const reasonLabel = (reason) => REASON_LABELS[String(reason || '').toLowerCase()] || reason || '—';

const RESOLUTION_LABELS = {
  refund: 'Refund',
  credit: 'Credit',
  replacement: 'Replacement',
  other: 'Other',
};

const resolutionLabel = (type) => RESOLUTION_LABELS[String(type || '').toLowerCase()] || 'Refund';

function isEomReturn(rr) {
  const paymentMethod = String(rr?.order?.paymentMethod || '').toLowerCase();
  return (
    String(rr?.resolutionType || '').toLowerCase() === 'credit' ||
    paymentMethod.includes('eom') ||
    paymentMethod.includes('monthly') ||
    paymentMethod.includes('end of month')
  );
}

export default function ReturnListScreen() {
  const { data, isLoading, isError, error, refetch } = useListReturnsQuery();
  const [updateStatus, { isLoading: isUpdating }] = useUpdateReturnStatusMutation();
  const [createLabel, { isLoading: isBuying }] = useCreateReturnLabelMutation();

  const [active, setActive] = useState(null); // modal return
  const [carrierFilter, setCarrierFilter] = useState('all');

  // rates mutations + local rate cache
  const [getRates, { isLoading: fetchingRates }] = useGetReturnRatesMutation();
  const [selectRate, { isLoading: picking }] = useSelectReturnRateMutation();
  const [rateData, setRateData] = useState(null); // { rates: [...] }

  const open = (rr) => {
    setActive(rr);
    setRateData(null); // reset previous rates when switching rows
  };
  const close = () => {
    setActive(null);
    setRateData(null);
  };

  const rows = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const onUpdate = async (fields) => {
    if (!active?._id) return;
    try {
      await updateStatus({ id: active._id, ...fields }).unwrap();
      toast.success('Updated');
      refetch();
      setActive((a) => (a ? { ...a, ...fields } : a)); // optimistic UI
    } catch (e) {
      toast.error(e?.data?.message || e?.message || 'Update failed');
    }
  };

  const onBuyLabel = async (carrierPref) => {
    if (!active?._id) return;
    try {
      const res = await createLabel({
        id: active._id,
        carriers: carrierPref?.length ? carrierPref : ['FedEx'],
        service: '',
      }).unwrap();
      toast.success('Label generated');
      refetch();
      if (res?.shippingLabelUrl) {
        window.open(res.shippingLabelUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (e) {
      toast.error(e?.data?.message || e?.message || 'Label failed');
    }
  };

  // fetch rates on demand
  const handleGetRates = async () => {
    if (!active?._id) return;
    try {
      const res = await getRates({ id: active._id, carriers: [], service: '' }).unwrap();
      setRateData(res || { rates: [] });
    } catch (e) {
      toast.error(e?.data?.message || e?.message || 'Failed to fetch rates');
    }
  };

  const handleChooseRate = async (rateId) => {
    if (!active?._id || !rateId) return;
    try {
      await selectRate({ id: active._id, rateId }).unwrap();
      toast.success('Rate selected');
      setActive((a) => (a ? { ...a, selectedRateId: rateId } : a));
    } catch (e) {
      toast.error(e?.data?.message || e?.message || 'Select failed');
    }
  };

  const listRates = useMemo(() => {
    const list = rateData?.rates || active?.rateOptions || [];
    if (carrierFilter === 'all') {
      return list.slice().sort((a, b) => Number(a.rate) - Number(b.rate));
    }
    return list
      .filter((r) => norm(r.carrier) === norm(carrierFilter))
      .slice()
      .sort((a, b) => Number(a.rate) - Number(b.rate));
  }, [rateData, active, carrierFilter]);

  return (
    <>
    <BackButton />
    <div className="py-3">
      <h1>Return Requests</h1>
      {isLoading ? (
        <p>Loading…</p>
      ) : isError ? (
        <p className="text-danger">
          {error?.data?.message || error?.error || 'Failed to load returns'}
        </p>
      ) : (
        <Table striped bordered hover responsive className="table-sm align-middle mt-3">
          <thead>
            <tr>
              <th>Created</th>
              <th>RMA</th>
              <th>Order Ref</th>
              <th>Customer</th>
              <th>Reason</th>
              <th>Type</th>
              <th>Status</th>
              <th>Label Cost</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center text-muted">
                  No return requests yet.
                </td>
              </tr>
            ) : (
              rows.map((rr) => {
                const orderRef =
                  rr?.order?.orderRef || rr?.order?.reference || rr?.order?._id || '—';
                return (
                  <tr key={rr._id}>
                    <td>{new Date(rr.createdAt).toLocaleString()}</td>
                    <td>{rr.rmaNumber || rr._id.slice(-6).toUpperCase()}</td>
                    <td>{orderRef}</td>
                    <td>
                      {rr.customerName}
                      <div style={{ fontSize: 12, color: '#666' }}>{rr.customerEmail}</div>
                    </td>
                    <td style={{ maxWidth: 320 }}>{reasonLabel(rr.reason)}</td>
                    <td>{resolutionLabel(rr.resolutionType)}</td>
                    <td>
                      <StatusBadge s={rr.status} />
                    </td>
                    <td>
                      {rr.labelCost != null
                        ? `$${Number(rr.labelCost).toFixed(2)} ${rr.labelCurrency || 'CAD'}`
                        : '—'}
                    </td>
                    <td>
                      <Button size="sm" variant="outline-primary" onClick={() => open(rr)}>
                        Manage
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      )}

      <Modal show={!!active} onHide={close} centered>
        <Modal.Header closeButton>
          <Modal.Title>Manage Return</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {active && (
            <>
              <p className="mb-2">
                <strong>RMA:</strong> {active.rmaNumber || active._id}
                <br />
                <strong>Order:</strong>{' '}
                {active?.order?.orderRef || active?.order?.reference || active?.order?._id}
                <br />
                <strong>Customer:</strong> {active.customerName} &lt;{active.customerEmail}&gt;
                <br />
                <strong>Type:</strong> {resolutionLabel(active.resolutionType)}
              </p>
              {isEomReturn(active) ? (
                <Alert variant="warning" className="py-2">
                  This EOM return should be handled as a monthly billing credit/adjustment unless payment has already been collected.
                </Alert>
              ) : null}
              <p style={{ whiteSpace: 'pre-wrap' }}>
                <strong>Reason:</strong> {reasonLabel(active.reason)}
                {active.details ? (
                  <>
                    <br />
                    <strong>Details:</strong> {active.details}
                  </>
                ) : null}
              </p>

              <Row className="g-2">
                <Col xs={12} className="mt-2">
                  <Form.Label>Status</Form.Label>
                  <div className="d-flex gap-2 flex-wrap">
                    {['authorized', 'rejected', 'received', 'refunded', 'partial_refunded', 'closed'].map(
                      (s) => (
                        <Button
                          key={s}
                          size="sm"
                          variant={active.status === s ? 'primary' : 'outline-secondary'}
                          disabled={isUpdating}
                          onClick={() => onUpdate({ status: s })}
                        >
                          {s}
                        </Button>
                      )
                    )}
                  </div>
                </Col>

                <Col xs={12} className="mt-3">
                  <Form.Label>Refund Amount (CAD)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    defaultValue={active.refundAmountApproved ?? ''}
                    onBlur={(e) => {
                      const v = Number(e.target.value);
                      if (!Number.isNaN(v)) onUpdate({ refundAmountApproved: v });
                    }}
                  />
                </Col>

                <Col xs={12} className="mt-3">
                  <Form.Label>RMA Number</Form.Label>
                  <Form.Control
                    type="text"
                    defaultValue={active.rmaNumber || ''}
                    onBlur={(e) => onUpdate({ rmaNumber: e.target.value })}
                  />
                </Col>

                <Col xs={12} className="mt-3">
                  <Form.Label>Tracking Code</Form.Label>
                  <Form.Control
                    type="text"
                    defaultValue={active.trackingCode || ''}
                    onBlur={(e) => onUpdate({ trackingCode: e.target.value })}
                  />
                </Col>

                <Col xs={12} className="mt-3">
                  <div className="d-flex align-items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline-success"
                      disabled={isBuying}
                      onClick={() => onBuyLabel(['FedEx'])}
                    >
                      {active?.selectedRateId ? 'Buy Selected Rate' : 'Buy Cheapest Rate'}
                    </Button>
                    {active.shippingLabelUrl ? (
                      <Button
                        size="sm"
                        variant="link"
                        onClick={() =>
                          window.open(active.shippingLabelUrl, '_blank', 'noopener,noreferrer')
                        }
                      >
                        Open current label
                      </Button>
                    ) : null}
                  </div>
                </Col>

                {/* Rate shopping UI */}
                <Col xs={12} className="mt-3">
                  <div className="d-flex gap-2 align-items-center flex-wrap">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={handleGetRates}
                      disabled={fetchingRates}
                    >
                      {fetchingRates ? 'Fetching rates…' : 'Get Rates (All Carriers)'}
                    </Button>
                    <Form.Select
                      size="sm"
                      style={{ width: 220 }}
                      value={carrierFilter}
                      onChange={(e) => setCarrierFilter(e.target.value)}
                    >
                      <option value="all">All carriers</option>
                      <option value="FedEx">FedEx</option>
                      <option value="Canada Post">Canada Post</option>
                      <option value="CanadaPost">CanadaPost</option>
                      <option value="UPS">UPS</option>
                    </Form.Select>
                  </div>

                  {listRates?.length ? (
                    <Table striped bordered hover size="sm" className="align-middle mt-2">
                      <thead>
                        <tr>
                          <th>Pick</th>
                          <th>Carrier</th>
                          <th>Service</th>
                          <th>ETA</th>
                          <th>Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {listRates.map((r) => {
                          const picked = active?.selectedRateId === r.id;
                          return (
                            <tr key={r.id} className={picked ? 'table-success' : ''}>
                              <td>
                                <Form.Check
                                  type="radio"
                                  name="returnRate"
                                  checked={picked}
                                  onChange={() => handleChooseRate(r.id)}
                                  disabled={picking}
                                />
                              </td>
                              <td>{r.carrier}</td>
                              <td>{r.service}</td>
                              <td>{r.estDays ? `${r.estDays}d` : '—'}</td>
                              <td>
                                ${Number(r.rate).toFixed(2)} {r.currency || 'CAD'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  ) : (
                    <div className="text-muted small mt-2">No rates yet — click “Get Rates”.</div>
                  )}
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={close}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
    </>
  );
}
